import React, { FC, useEffect, useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useMutation, useQuery } from 'urql';

import Comments from '../../components/Comments';
import { PREFERENCE__COMMENTS__LIMIT } from '../../constants/pagination';
import {
  CommentCreateDocument,
  CommentsDocument,
} from '../../generated/graphql';
import translate from '../../i18n/translate';
import { Comment } from '../../models/comment';
import { reportError } from '../../services/reporting';

import { reportApiErrors } from '../../utils/error';

import mapDispatchToProps from './mapDispatchToProps';
import mapStateToProps from './mapStateToProps';
import { Props } from './props';

/**
 * Comments related to a message
 *
 * @param props                            Props passed to the component
 * @param props.isArchived                 Flag that indicate if message is archived
 * @param props.messageId                  The message to show the comments for
 * @param props.networkComments            Whether to override GraphQL fetching state (debug option)
 * @param props.numberOfComments           Number of comments
 * @param props.requestSubscribeToComments Requests a graphql subscription to the comments for a message
 * @param props.scrollIntoViewCallback     Callback that calls scrollIntoView on the selected message
 * @param props.thread                     Metadata about the comment thread (for external messaging only)
 * @returns                                The component itself
 */
const CommentsContainer: FC<Props> = ({
  isArchived,
  messageId,
  networkComments,
  numberOfComments,
  requestSubscribeToComments,
  scrollIntoViewCallback,
  thread = null,
}) => {
  const [text, setDraftText] = useState('');
  const [cursor, setCursor] = useState('');
  const [isNewComment, setIsNewComment] = useState(false);
  const [, commentCreateMutation] = useMutation(CommentCreateDocument);

  useEffect(() => {
    /**
     * Subscribes to comments when the comments have been opened for the first time
     */
    requestSubscribeToComments(messageId);
  }, [messageId, requestSubscribeToComments]);

  const [{ data, error, fetching }, refetchComments] = useQuery({
    // Backend doesn't allow fetching of comments for archived messages
    pause: isArchived === true,
    query: CommentsDocument,
    variables: {
      after: cursor,
      first: PREFERENCE__COMMENTS__LIMIT,
      messageId,
    },
  });

  /**
   * !!This is a monkey patch!!.
   * What is the purpose of this?
   * When creating a new topic, and a new message, and creating a new reply,
   * the commentInfo subscription doesn't arrive to the FE (even tho it's active).
   * Since messageInfo is active, we can measure numberOfComments and use that
   * to refetch the comments query. This way works both for the initiator and the user looking at messages.
   * This solution is not ideal as every time new comment arrives we will have 1 extra fetch.
   *
   * @todo replace this with working sub when subscriptions are investigated.
   */
  useEffect(() => {
    // Override the original cache-only policy
    refetchComments({ requestPolicy: 'cache-and-network' });
  }, [numberOfComments, refetchComments]);

  const isLoading = fetching === true || networkComments === true;

  /**
   * Request that a message is submitted
   *
   * @param isForExternal Whether the message is intended for team members or the guest that posted the message
   */
  const requestSubmit = (isForExternal: boolean) => {
    commentCreateMutation({ isForExternal, messageId, text }).catch(
      reportApiErrors,
    );
    setDraftText('');
    setIsNewComment(true);
  };

  useLayoutEffect(() => {
    /**
     * Comments have been expanded and fetching has been completed
     * and we scroll that message into view
     */
    if (scrollIntoViewCallback && isLoading === false) {
      scrollIntoViewCallback();
    }
  }, [isLoading, scrollIntoViewCallback]);

  if (error) {
    reportError(error);
    return <div>{translate('PLACEHOLDER__ERROR')}</div>;
  }

  /**
   * The user has clicked to load more comments,
   * So we set the pagination cursor to reflect that
   */
  const loadMore = () => {
    setIsNewComment(false);
    setCursor(data?.comments.pageInfo.endCursor ?? '');
  };

  // Cache is not sorting as it should so we are sorting here atm.
  /**
   * @todo - find a better solution for this, this sort is an expensive operation
   */
  const nodes = data?.comments.nodes.sort((nodeA: Comment, nodeB: Comment) => {
    return nodeA.createdAt.localeCompare(nodeB.createdAt);
  });

  return (
    <Comments
      comments={(nodes as Comment[]) ?? []}
      draftText={text}
      isArchived={isArchived}
      isLoading={isLoading}
      isNewComment={isNewComment}
      requestDraftUpdate={setDraftText}
      requestLoadMore={data?.comments.pageInfo.hasNextPage ? loadMore : null}
      requestSubmit={requestSubmit}
      thread={thread}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CommentsContainer);
